import { useOptionalPrivanaI18n } from '@/i18n/PrivanaI18nProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import MemberMobileBookingHeader from '../components/MemberMobileBookingHeader'
import { DEFAULT_GOLF_TIME_ZONE, formatLongDate } from '../utils/golfTime'
import { getFitnessMemberErrorMessage } from '../utils/fitnessMemberErrors'

const CURRENCY_SYMBOLS = {
  GBP: '£',
  USD: '$',
  EUR: '€',
}

function formatPrice(value, currency, locale) {
  if (value == null || value === '') return null
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return null
  const code = currency || ''
  const symbol = CURRENCY_SYMBOLS[code] || ''
  const formatted = numeric.toLocaleString(locale, { minimumFractionDigits: numeric % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })
  if (symbol) return `${symbol}${formatted}`
  if (code) return `${formatted} ${code}`
  return formatted
}

function formatDuration(durationMinutes, locale) {
  if (!Number.isFinite(durationMinutes)) return null
  return new Intl.NumberFormat(locale, { style: 'unit', unit: 'minute', unitDisplay: 'short' }).format(durationMinutes)
}

function formatRemaining(session, memberText) {
  if (Number.isInteger(session?.remaining)) {
    return memberText("portalPresentation.member__count_left", { count: session.remaining })
  }
  if (session?.capacity) return memberText("portalPresentation.member_limited_availability")
  return memberText("portalPresentation.available")
}

function formatSessionTime(session) {
  const start = session?.start_time || null
  const end = session?.end_time || null
  if (start && end) return `${start} - ${end}`
  return start || end || null
}

export default function MemberFitnessClassesPage({ clubCode }) {
  const { t: memberText, locale } = useOptionalPrivanaI18n()

  const navigate = useNavigate()
  const { memberPortalApiDelete, memberPortalApiGet } = useMemberPortalApi()
  const [schedule, setSchedule] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionKey, setActionKey] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function loadSchedule() {
      if (!clubCode) return
      setLoading(true)
      setError(null)

      try {
        const res = await memberPortalApiGet(`/api/members-portal/${clubCode}/fitness/schedule`)
        const json = await res.json().catch(() => null)

        if (!res.ok) {
          throw new Error(getFitnessMemberErrorMessage(json?.error))
        }

        if (!cancelled) {
          setSchedule(json?.data || null)
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load fitness schedule', err)
          setError(getFitnessMemberErrorMessage(err?.message, key => key))
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadSchedule()

    return () => {
      cancelled = true
    }
  }, [clubCode, memberPortalApiGet])

  const timeZone = schedule?.time_zone || DEFAULT_GOLF_TIME_ZONE
  const dates = useMemo(() => (Array.isArray(schedule?.dates) ? schedule.dates : []), [schedule])

  const handleBook = (session, dateKey) => {
    if (!session?.class_id || !dateKey) return
    const params = new URLSearchParams({ date: dateKey, start_at: session.start_at, start_time: session.start_time, occurrence_id: session.occurrence_id })
    navigate(`/${clubCode}/fitness/${session.class_id}/book?${params}`, {
      state: {
        slot: session,
        fitnessClass: {
          id: session.class_id,
          name: session.name,
          description: session.description,
          instructor: session.instructor,
          location: session.location,
          duration_minutes: session.duration_minutes,
          capacity: session.capacity,
          photo_url: session.photo_url,
          price: session.price,
          currency: session.currency,
        },
      },
    })
  }

  const handleCancel = async (session) => {
    if (!session?.current_member_booking_id) return
    const slotKey = `${session.class_id}-${session.start_at}`
    setActionKey(slotKey)
    setError(null)
    try {
      const res = await memberPortalApiDelete(
        `/api/members-portal/${clubCode}/fitness/bookings/${session.current_member_booking_id}`
      )
      const json = await res.json().catch(() => null)
      if (!res.ok) {
        throw new Error(getFitnessMemberErrorMessage(json?.error))
      }

      const refreshRes = await memberPortalApiGet(`/api/members-portal/${clubCode}/fitness/schedule`)
      const refreshJson = await refreshRes.json().catch(() => null)
      if (!refreshRes.ok) {
        throw new Error(getFitnessMemberErrorMessage(refreshJson?.error))
      }
      setSchedule(refreshJson?.data || null)
    } catch (err) {
      console.error('Failed to cancel fitness booking', err)
      setError(getFitnessMemberErrorMessage(err?.message, key => key))
    } finally {
      setActionKey(null)
    }
  }

  return (
    <div className="mp-panel">
      <MemberMobileBookingHeader
        className="mp-panel__header mp-fitness-classes-header"
        eyebrow={memberText("portalPresentation.member_fitness")}
        title={memberText("portalPresentation.member_classes")}
        desktopTitle={memberText("portalPresentation.member_book_a_class")}
        backLabel={memberText("portalPresentation.member_back_to_facilities")}
        backTo={`/${clubCode}/book-facilities`}
      />

      {error ? <div className="mp-warning" role="alert">{memberText(error)}</div> : null}

      {loading ? (
        <p className="mp-text-muted">{memberText("portalPresentation.member_loading_schedule")}</p>
      ) : dates.length === 0 ? (
        <p className="mp-text-muted">{memberText("portalPresentation.member_no_upcoming_fitness_classes_with_availability")}</p>
      ) : (
        <div className="mp-fitness-schedule-list">
          {dates.map((dateEntry) => {
            const formattedDate = formatLongDate(dateEntry.date, timeZone, locale)

            return (
              <div key={dateEntry.date} className="mp-fitness-schedule-date">
                <div className="mp-fitness-schedule-date__header">
                  <p className="mp-eyebrow">{formattedDate || dateEntry.date}</p>
                  <span className="mp-fitness-schedule-date__count">
                    {dateEntry.sessions?.length || 0} {memberText("portalPresentation.member_sessions")} </span>
                </div>
                <div className="mp-fitness-session-list">
                  {dateEntry.sessions?.map((session) => {
                    const duration = formatDuration(session?.duration_minutes, locale)
                    const price = formatPrice(session?.price, session?.currency, locale)
                    const remaining = formatRemaining(session, memberText)
                    const timeRange = formatSessionTime(session)
                    const slotKey = `${session.class_id}-${session.start_at}`
                    const isBusy = actionKey === slotKey
                    const isBooked = session?.is_booked_by_current_member === true

                    return (
                      <div
                        key={`${session.class_id}-${session.start_at}`}
                        className="mp-golf-course-card mp-fitness-session-card"
                      >
                        <div className="mp-fitness-session-card__image-wrapper">
                          {session.photo_url ? (
                            <img
                              className="mp-fitness-session-card__image"
                              src={session.photo_url}
                              alt=""
                              loading="lazy"
                              onError={(event) => {
                                event.currentTarget.style.display = 'none'
                              }}
                            />
                          ) : null}
                        </div>
                        <div className="mp-fitness-session-card__details">
                          <p className="mp-golf-course-label">{timeRange || memberText("portalPresentation.member_scheduled_class")}</p>
                          <div className="mp-fitness-session-card__name">{session?.name || memberText("portalPresentation.member_fitness_class")}</div>
                          <div className="mp-fitness-session-card__meta">
                            {duration ? <span>{duration}</span> : null}
                            {session?.location ? <span>{session.location}</span> : null}
                            {session?.instructor ? <span>{memberText("portalPresentation.member_coach")} {session.instructor}</span> : null}
                            {price ? <span className="mp-spa-service-card__price">{price}</span> : null}
                          </div>
                          {session?.description ? (
                            <p className="mp-fitness-session-card__description">{session.description}</p>
                          ) : null}
                        </div>
                        <div className="mp-fitness-session-card__action">
                          <div className="mp-fitness-session-card__remaining">{remaining}</div>
                          {isBooked ? (
                            <button
                              type="button"
                              className="mp-btn mp-btn--outline mp-btn--pill"
                              onClick={() => handleCancel(session)}
                              disabled={isBusy}
                            >
                              {isBusy ? memberText("compliance.copy.cancelling") : memberText("portalPresentation.member_cancel")}
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="mp-btn mp-btn--primary mp-btn--pill"
                              onClick={() => handleBook(session, dateEntry.date)}
                              disabled={isBusy}
                            > {memberText("academyP2.book")} </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
